import {
  Show,
  FunctionField,
  useRecordContext,
  useShowContext,
  TabbedShowLayout,
  Tab,
  List,
  Datagrid,
  DateField,
  useDataProvider,
  useNotify,
  useRefresh,
  TextField,
  NumberField,
  useRedirect,
} from 'react-admin';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import {
  Box,
  Chip,
  Typography,
  Grid,
  Avatar,
  Stack,
  Card,
  CardContent,
  List as MuiList,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Paper,
} from '@mui/material';
import {
  Person as PersonIcon,
  Flag as PriorityIcon,
  Link as LinkIcon,
  Attachment,
  Timer as TimerIcon,
  StopCircle as StopIcon,
  Repeat as RepeatIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { TaskDialog } from '../../components/TaskDialog';
import { Link as RouterLink } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { tenantService } from '../../services/tenantService';
import { AddMaterialDialog } from './AddMaterialDialog';
import { StopTimerButton } from '../../components/StopTimerButton';
import { formatDurationSeconds } from '../../utils/formatDurationSeconds';

// Centralized date formatting function
const formatDateTime = (dateString?: string) => {
  if (!dateString) { return null; }
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const formatDateOnly = (dateString?: string) => {
  if (!dateString) { return null; }
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Actions are now integrated into ResourceBreadcrumbsWithActions
const PriorityChip = ({ record }: { record?: { priority?: string } }) => {
  if (!record?.priority) {
    return <span>-</span>;
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Lowest':
        return '#94A3B8';
      case 'Low':
        return '#10B981';
      case 'Medium':
        return '#3B82F6';
      case 'High':
        return '#F59E0B';
      case 'Highest':
        return '#EF4444';
      case 'Critical':
        return '#DC2626';
      default:
        return '#94A3B8';
    }
  };

  return (
    <Chip
      label={record.priority}
      size="medium"
      sx={{
        backgroundColor: getPriorityColor(record.priority),
        color: 'white',
        fontWeight: 'bold',
      }}
      icon={<PriorityIcon sx={{ color: 'white !important' }} />}
    />
  );
};

const AssigneeCard = ({ record }: { record?: { assignees?: any[]; assignee?: { name?: string; email?: string; avatarUrl?: string } } }) => {
  const assignees = record?.assignees && record.assignees.length > 0
    ? record.assignees
    : record?.assignee
      ? [record.assignee]
      : [];

  if (assignees.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Assignees
          </Typography>
          <Typography color="text.secondary">
            No assignees
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Assignees
        </Typography>
        <Stack spacing={2}>
          {assignees.map((assignee: any, index: number) => (
            <Box key={assignee.id || index} display="flex" alignItems="center" gap={2}>
              <Avatar
                src={assignee.avatarUrl || assignee.avatar}
                sx={{ width: 40, height: 40 }}
              >
                {assignee.name?.[0]?.toUpperCase() || <PersonIcon />}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {assignee.name || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {assignee.email}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const EntityLinkCard = ({ record }: { record?: { entityType?: string; entityId?: string } }) => {
  if (!record?.entityType || !record?.entityId) {
    return null;
  }

  const getEntityDetails = (entityType: string) => {
    switch (entityType) {
      case 'customer':
        return { label: 'Customer', icon: '👤', color: '#3B82F6' };
      case 'equipment':
        return { label: 'Equipment', icon: '🚗', color: '#10B981' };
      case 'opportunity':
        return { label: 'Opportunity', icon: '💼', color: '#F59E0B' };
      case 'driver':
        return { label: 'Driver', icon: '🚛', color: '#8B5CF6' };
      default:
        return { label: entityType, icon: '📄', color: '#6B7280' };
    }
  };

  const entityDetails = getEntityDetails(record.entityType);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Related To
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              backgroundColor: entityDetails.color,
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
            }}
          >
            {entityDetails.icon}
          </Box>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              {entityDetails.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {record.entityId}
            </Typography>
          </Box>
          <IconButton size="small" color="primary">
            <LinkIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const TaskDetailsTab = () => {
  const record = useRecordContext();
  const isScheduledSoon = record?.scheduledDate && new Date(record.scheduledDate) <= new Date(Date.now() + 24 * 60 * 60 * 1000);

  return (
    <Box p={2}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent >
              <Stack spacing={3}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    Task Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record?.title}
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record?.description || 'No description provided'}
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    Customer
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record.customer ? (
                      <RouterLink to={`/customers/${record.customer.id}/show`} style={{ textDecoration: 'none' }}>
                        {record.customer.displayName || record.customer.company}
                      </RouterLink>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record.customerLocation ? (
                      <RouterLink to={`/customers/${record.customer.id}/show`} style={{ textDecoration: 'none' }}>
                        {record.customerLocation?.location?.name}
                        <br />
                        {record.customerLocation?.location?.streetLine1}

                        {record.customerLocation?.location?.streetLine2 && (
                          <>
                            <br />
                            {record.customerLocation.location.streetLine2}
                          </>
                        )}
                        <br />
                        {
                          [
                            record.customerLocation?.location?.postalCode,
                            record.customerLocation?.location?.province,
                            record.customerLocation?.location?.country,
                          ]
                            .filter(p => p && String(p).trim() !== '')
                            .join(', ')
                        }
                      </RouterLink>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Details
                </Typography>

                {record?.scheduledDate && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pt: 1,
                      alignItems: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Scheduled Date
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: isScheduledSoon ? 'warning.main' : 'inherit',
                        fontWeight: isScheduledSoon ? 'bold' : 'normal',
                      }}
                    >
                      {formatDateTime(record.scheduledDate)}
                      {isScheduledSoon && ' (Soon)'}
                    </Typography>
                  </Box>
                )}

                {record?.estimatedHours && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pt: 3,
                      alignItems: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Estimated Duration (hours)
                    </Typography>
                    <Typography variant="body1">
                      {(() => {
                        const rawDuration = record?.estimatedHours;
                        if (rawDuration === null) {
                          return '-';
                        }
                        const duration = Number.isInteger(rawDuration)
                          ? rawDuration
                          : parseFloat(rawDuration.toFixed(2));
                        return `${duration}h`;
                      })()}
                    </Typography>
                  </Box>
                )}

                {record?.taskStatus && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pt: 3,
                      alignItems: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={record.taskStatus?.name}
                      size="medium"
                      sx={{
                        backgroundColor: record.taskStatus?.color,
                        color: (() => {
                          const hex = record.taskStatus?.color;
                          if (!hex) { return 'inherit'; }
                          const c = hex.replace('#', '');
                          if (c.length !== 6) { return 'inherit'; }
                          const r = parseInt(c.slice(0, 2), 16);
                          const g = parseInt(c.slice(2, 4), 16);
                          const b = parseInt(c.slice(4, 6), 16);
                          // YIQ formula to determine contrasting text color
                          const yiq = (r * 299 + g * 587 + b * 114) / 1000;
                          return yiq >= 128 ? 'rgba(0,0,0,0.87)' : '#fff';
                        })(),
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                )}

                {record?.priority && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pt: 3,
                      alignItems: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Priority
                    </Typography>
                    <PriorityChip record={record as any} />
                  </Box>
                )}

                {record?.category && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pt: 3,
                      alignItems: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1">
                      {record?.category?.name || '-'}
                    </Typography>
                  </Box>
                )}

                {record?.recurrenceFrequency && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pt: 3,
                      alignItems: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Recurrence
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RepeatIcon fontSize="small" color="action" />
                      <Typography variant="body1">
                        {record.recurrenceFrequency}
                        {record.recurrenceEndDate && ` until ${new Date(record.recurrenceEndDate).toLocaleDateString()}`}
                      </Typography>
                    </Box>
                  </Box>
                )}

              </CardContent>
            </Card>

            <AssigneeCard record={record as any} />

            <EntityLinkCard record={record as any} />

            {record?.tags && record.tags.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tags
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {record.tags.map((tag: string, index: number) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};



const UserField = ({ record }: { record?: any }) => {
  if (!record?.user) {
    return <span>-</span>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar
        src={record.user.avatarUrl || record.user.avatarThumbnail}
        sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
      >
        {record.user.name?.[0]?.toUpperCase() || record.user.email?.[0]?.toUpperCase() || <PersonIcon />}
      </Avatar>
      <span>{record.user.name || record.user.email || '-'}</span>
    </Box>
  );
};



const DurationField = ({ record }: { record?: any }) => {
  const [elapsedTime, setElapsedTime] = useState<string>('-');
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (!record?.startedAt) {
      setElapsedTime('-');
      setIsRunning(false);
      return;
    }

    const start = new Date(record.startedAt);
    if (isNaN(start.getTime())) {
      setElapsedTime('-');
      setIsRunning(false);
      return;
    }

    // Check if timer is running (endedAt is null)
    if (!record?.endedAt) {
      setIsRunning(true);

      const updateElapsedTime = () => {
        const now = new Date();
        const diffMs = now.getTime() - start.getTime();

        if (diffMs < 0) {
          setElapsedTime('-');
          return;
        }

        const totalSeconds = Math.floor(diffMs / 1000);
        setElapsedTime(formatDurationSeconds(totalSeconds));
      };

      // Update immediately
      updateElapsedTime();

      // Update every second
      const interval = setInterval(updateElapsedTime, 1000);

      return () => clearInterval(interval);
    } else {
      // Timer has ended, calculate final duration
      setIsRunning(false);
      const end = new Date(record.endedAt);

      if (isNaN(end.getTime())) {
        setElapsedTime('-');
        return;
      }

      const diffMs = end.getTime() - start.getTime();
      if (diffMs < 0) {
        setElapsedTime('-');
        return;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      setElapsedTime(formatDurationSeconds(totalSeconds));
    }
  }, [record?.startedAt, record?.endedAt]);

  if (elapsedTime === '-') {
    return <span>-</span>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isRunning && <TimerIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
      <span>{elapsedTime}</span>
    </Box>
  );
};

const TimeEntriesTab = () => {
  const record = useRecordContext();

  if (!record?.id) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <List
      resource="timetracking/segments"
      filter={{ taskId: record.id }}
      perPage={25}
      sort={{ field: 'endedAt', order: 'DESC' }}
      sx={{ '& .RaList-main': { mt: 0 } }}
      exporter={false}
      disableSyncWithLocation
      storeKey={false}
      empty={
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Typography color="text.secondary" textAlign="center" py={4}>
            No time tracking segments recorded yet for this task.
          </Typography>
        </Box>
      }
    >
      <Datagrid bulkActionButtons={false}>
        <DateField source="startedAt" label="Start Time" showTime />
        <DateField source="endedAt" label="End Time" showTime />
        <FunctionField
          label="Duration"
          render={(record: any) => <DurationField record={record} />}
        />
        <FunctionField
          label="User"
          render={(record: any) => <UserField record={record} />}
        />
        <FunctionField
          label="Actions"
          render={(record: any) => {
            // Only show stop button if timer is running (endedAt is null)
            if (!record?.endedAt) {
              return <StopTimerButton record={record} />;
            }
            return null;
          }}
        />
      </Datagrid>
    </List>
  );
};


const AttachmentList = ({ attachments }: { attachments: any[] }) => {
  if (!attachments || attachments.length === 0) return null;
  const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';

  return (
    <MuiList disablePadding sx={{ mt: 1, width: '100%' }}>
      {attachments.map((attachment: any) => (
        <ListItem
          key={attachment.id}
          disableGutters
          sx={{ py: 1 }}
        >
          <a
            href={`${apiUrl}/files/${attachment.id}/content`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', marginRight: '10px' }}
          >
            {(attachment.thumbnail) ? (
              <Box
                sx={{
                  backgroundImage: `url(data:${attachment.mimeType};base64,${attachment.thumbnail})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  width: 80,
                  height: 56,
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                }}
              >
                <Attachment fontSize="large" color="action" />
              </Box>
            )}
          </a>
          <ListItemText
            primary={
              <Typography variant="subtitle2" fontWeight="bold">
                {attachment.name || attachment.file?.name || `Attachment ${attachment.id}`}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </MuiList>
  );
};

const TimelineTab = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageRef = useRef(1);
  const perPage = 10;

  // Keep refs in sync with state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const loadTimelineData = useCallback(async (pageNum: number, append: boolean = false) => {
    if (!record?.id || loadingRef.current || !hasMoreRef.current) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const whereFilter = JSON.stringify({ taskId: record.id });
      const url = `${baseUrl}/tasks-status-history/status-history?where=${encodeURIComponent(whereFilter)}&page=${pageNum}&perPage=${perPage}&orderByField=createdAt&order=desc`;

      const accessToken = localStorage.getItem('accessToken');
      const headers: HeadersInit = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      // Add tenant code header
      try {
        const tenantCode = tenantService.getCurrentTenantCode();
        headers['x-tenant-code'] = tenantCode;
      } catch (error) {
        // Tenant service might not be available, continue without it
      }

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch timeline: ${response.statusText}`);
      }

      const json = await response.json();
      const newData = json.data || [];

      if (append) {
        setTimelineData(prev => {
          const updated = [...prev, ...newData];
          // Check if there's more data to load
          const total = json.metadata?.total || json.total || 0;
          const more = updated.length < total && newData.length === perPage;
          hasMoreRef.current = more;
          setHasMore(more);
          return updated;
        });
      } else {
        setTimelineData(newData);
        // Check if there's more data to load
        const total = json.metadata?.total || json.total || 0;
        const more = newData.length < total && newData.length === perPage;
        hasMoreRef.current = more;
        setHasMore(more);
      }
    } catch (error: any) {
      notify(error.message || 'Failed to load timeline', { type: 'error' });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [record?.id, notify, perPage]);

  // Load statuses for status name lookup
  useEffect(() => {
    const loadStatuses = async () => {
      if (!record?.workflowId) {
        return;
      }

      try {
        const { data: statuses } = await dataProvider.getList('task-statuses', {
          filter: { workflowId: record.workflowId },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'orderIndex', order: 'ASC' },
        });

        const map: Record<string, string> = {};
        statuses.forEach((status: any) => {
          map[status.id] = status.name;
        });
        setStatusMap(map);
      } catch (error) {
        // Silently fail - status names are optional
      }
    };

    loadStatuses();
  }, [record?.workflowId, dataProvider]);

  // Initial load
  useEffect(() => {
    if (record?.id) {
      setTimelineData([]);
      pageRef.current = 1;
      hasMoreRef.current = true;
      setHasMore(true);
      loadingRef.current = false;
      setLoading(false);
      loadTimelineData(1, false);
    }
  }, [record?.id, loadTimelineData]);

  // Scroll handler for infinite loading
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    let timeoutId: number | null = null;

    const handleScroll = () => {
      // Debounce scroll events
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        if (loadingRef.current || !hasMoreRef.current) {
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;
        const threshold = 100; // Load more when 100px from bottom

        if (scrollHeight - scrollTop - clientHeight < threshold) {
          pageRef.current += 1;
          loadTimelineData(pageRef.current, true);
        }
      }, 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [loadTimelineData]);

  if (!record?.id) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        height: 'calc(100vh - 300px)',
        overflowY: 'auto',
        p: 2,
      }}
    >
      {timelineData.length === 0 && !loading ? (
        <Typography color="text.secondary" textAlign="center" py={4}>
          No timeline entries yet
        </Typography>
      ) : (
        <MuiList>
          {timelineData.map((entry: any) => (
            <ListItem key={entry.id} alignItems="flex-start" sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 2 }}>
              <ListItemAvatar>
                <Avatar
                  src={entry.user?.avatarUrl || entry.user?.avatarThumbnail}
                  sx={{ width: 40, height: 40 }}
                >
                  {entry.user?.name?.[0]?.toUpperCase() || entry.user?.email?.[0]?.toUpperCase() || <PersonIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ pb: 2 }}>
                    <Typography variant="body1" fontWeight="bold" component="span">
                      {entry.user?.name || entry.user?.email || 'Unknown User'}
                      &nbsp;
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" component="span">
                      {entry.type === 'TaskUpdated' ? 'edited the task' : ''}
                      {entry.type === 'StatusChange' ? (
                        <>
                          changed status to{' '}
                          {entry.toStatusId && statusMap[entry.toStatusId] ? (
                            <Typography variant="body1" fontWeight="bold" component="span" sx={{ color: 'primary.main' }}>
                              {statusMap[entry.toStatusId]}
                            </Typography>
                          ) : (
                            'Unknown Status'
                          )}
                        </>
                      ) : ''}
                      {entry.type === 'Note' ? 'added a note' : ''}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary" component="span">
                      {formatDateTime(entry.createdAt)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box component="span">
                    {entry.note && entry.note !== '' && (
                      <Typography variant="body1" sx={{ mb: 0.5 }} component="div">
                        {entry.note}
                      </Typography>
                    )}
                    {entry.type === 'Note' && entry.attachments && entry.attachments.length > 0 && (
                      <AttachmentList attachments={entry.attachments} />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
          {loading && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography color="text.secondary" textAlign="center" component="span">
                    Loading more...
                  </Typography>
                }
              />
            </ListItem>
          )}
        </MuiList>
      )}
    </Box>
  );
};

const TaskTitle = () => {
  const record = useRecordContext();
  if (!record) { return 'Loading...'; }

  return record.title || record.name || 'Task';
};

const MaterialsTab = () => {
  const record = useRecordContext();

  const [addMaterialOpen, setAddMaterialOpen] = useState(false);
  const refresh = useRefresh();

  if (!record?.materials || record.materials.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 2, pb: 4 }}>
        <Typography color="text.secondary" textAlign="center" py={4}>
          No materials yet
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setAddMaterialOpen(true)}
        >
          Add Actuals
        </Button>
        {record?.id && (
          <AddMaterialDialog
            open={addMaterialOpen}
            onClose={() => setAddMaterialOpen(false)}
            taskId={String(record.id)}
            onSuccess={() => {
              setAddMaterialOpen(false);
              refresh();
            }}
          />
        )}
      </Box>
    );
  }

  const sortedMaterials = [...record.materials].sort((a: any, b: any) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <Box sx={{ pt: 4, pb: 10 }}>
      <Paper sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Datagrid
          data={sortedMaterials}
          bulkActionButtons={false}
          sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}
          sort={{ field: 'createdAt', order: 'DESC' }}
        >
          <TextField source="material.name" label="Material Name" />
          <NumberField source="quantity" label="Quantity" />
          <NumberField
            source="cost"
            label="Cost"
            options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
          <TextField source="createdBy.name" label="Created By" />
          <DateField source="createdAt" label="Created At" showTime />
        </Datagrid>
      </Paper>

      <div style={{ padding: '20px 0' }} />
      <Button
        variant="outlined"
        size="small"
        onClick={() => setAddMaterialOpen(true)}
      >
        Add Actuals
      </Button>
      {record?.id && (
        <AddMaterialDialog
          open={addMaterialOpen}
          onClose={() => setAddMaterialOpen(false)}
          taskId={String(record.id)}
          onSuccess={() => {
            setAddMaterialOpen(false);
            refresh();
          }}
        />
      )}
    </Box>
  );
};

const AttachmentsTab = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box>
      {record?.attachments?.length > 0 ? (
        <MuiList>
          {record.attachments.map((attachment: any) => (
            <ListItem
              key={attachment.id}
            >
              <a href={attachment.file.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                {attachment.file?.thumbnail ? (
                  <Box
                    sx={{
                      display: 'inline-block',
                      marginRight: '10px',
                      backgroundImage: `url(data:${attachment.file?.mimeType};base64,${attachment.file?.thumbnail})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      width: 80,
                      height: 56,
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      width: 80,
                      height: 56,
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <Attachment fontSize="large" color="action" />
                    </ListItemIcon>
                  </Box>
                )}
              </a>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {attachment.file?.name || `Attachment ${attachment.id}`}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {attachment.uploadedBy?.name || 'Unknown User'}
                    {' • '}
                    {formatDateTime(attachment.createdAt)}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </MuiList>
      ) : (
        <Typography color="text.secondary" textAlign="center" py={4}>
          No attachments yet
        </Typography>
      )}
    </Box>
  );
};

const TaskShowContent = () => (
  <TabbedShowLayout>
    <Tab label="Overview">
      <TaskDetailsTab />
    </Tab>

    <Tab label="Materials">
      <MaterialsTab />
    </Tab>

    <Tab label="Attachments">
      <AttachmentsTab />
    </Tab>

    <Tab label="Timeline">
      <TimelineTab />
    </Tab>

    <Tab label="Time Tracking">
      <TimeEntriesTab />
    </Tab>
  </TabbedShowLayout>
);

const TaskShowLayout = () => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { record, isLoading, error } = useShowContext();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const [taskToEdit, setTaskToEdit] = useState<any>(null);

  if (error || (!isLoading && !record)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', gap: 2 }}>
        <ErrorIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
        <Typography variant="h5" color="text.secondary">
          Task not found
        </Typography>
        <Button variant="contained" onClick={() => redirect('/tasks')}>
          Back to Tasks
        </Button>
      </Box>
    );
  }

  const handleEdit = () => {
    setTaskToEdit(record);
    setEditDialogOpen(true);
  };

  const handleClose = () => {
    setEditDialogOpen(false);
    // Reset taskToEdit after animation/close
    setTimeout(() => setTaskToEdit(null), 300);
  };

  const handleSwitchTask = (newTask: any) => {
    setEditDialogOpen(false);
    // Small delay to allow dialog to close before reopening with new data
    setTimeout(() => {
      setTaskToEdit(newTask);
      setEditDialogOpen(true);
    }, 300);
  };

  return (
    <Box>
      <ResourceBreadcrumbsWithActions onEdit={handleEdit} hideDelete={true} />
      <TaskShowContent />
      {record && (
        <TaskDialog
          open={editDialogOpen}
          onClose={handleClose}
          task={taskToEdit || record}
          onSuccess={() => {
            refresh();
            setEditDialogOpen(false);
            setTaskToEdit(null);
          }}
          onDelete={() => {
            // Task has been deleted.
            // We need to reload the page to clear the current record from the cache
            // and trigger the "Task not found" error state layout.
            window.location.reload();
          }}
          onSwitchTask={handleSwitchTask}
          onMaterialized={(newTask) => {
            setEditDialogOpen(false);
            redirect('/tasks/' + newTask.id + '/show');
          }}
        />
      )}
    </Box>
  );
};

export const TaskShow = () => (
  <Show
    actions={false}
    title={<TaskTitle />}
    queryOptions={{
      onError: (err) => {
        // Prevent default error handling (notification and redirect)
        // so we can show our custom error component
        console.error('TaskShow error:', err);
      },
      retry: 0,
    }}
  >
    <TaskShowLayout />
  </Show>
);